import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Globe } from "lucide-react";
import { apiFetch, isEmailAuthMode } from "@/lib/api-client";

const LANGS = [
  { code: "hu" as const, key: "language.hu" },
  { code: "sk" as const, key: "language.sk" },
  { code: "en" as const, key: "language.en" },
] as const;

interface LanguageSwitcherProps {
  className?: string;
  variant?: "select" | "compact";
}

export default function LanguageSwitcher({ className, variant = "select" }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();

  const current = LANGS.find((l) => l.code === i18n.language)?.code ?? "hu";

  const handleChange = async (value: string) => {
    if (value !== "hu" && value !== "sk" && value !== "en") return;
    i18n.changeLanguage(value);
    // Persist to user preferences so the same language is used on all devices
    if (isEmailAuthMode()) {
      try {
        const res = await apiFetch("/api/user/preferences", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ preferences: { locale: value } }),
        });
        if (!res.ok && import.meta.env.DEV) {
          console.warn("[LanguageSwitcher] Saving locale returned", res.status);
        }
      } catch (err) {
        console.error("[LanguageSwitcher] Failed to save locale to preferences:", err);
      }
    }
  };

  if (variant === "compact") {
    return (
      <Select value={current} onValueChange={handleChange}>
        <SelectTrigger className={className ?? "w-[130px]"} aria-label={t("language.label")}>
          <Globe className="mr-2 h-4 w-4 shrink-0" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {LANGS.map((l) => (
            <SelectItem key={l.code} value={l.code}>
              {t(l.key)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Select value={current} onValueChange={handleChange}>
      <SelectTrigger className={className} aria-label={t("language.label")}>
        <Globe className="mr-2 h-4 w-4 shrink-0" />
        <SelectValue placeholder={t("language.label")} />
      </SelectTrigger>
      <SelectContent>
        {LANGS.map((l) => (
          <SelectItem key={l.code} value={l.code}>
            {t(l.key)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
